/**
 * Touch hit areas for small controls (#2420 / #2602 / #2603, ruling D-2420
 * option A). One module for staff and parent screens since #2624; the
 * parent-chrome copy (`touch-target-parent.ts`, `TOUCH_HIT`) was folded in.
 *
 * The drawn box, hover fill and focus ring keep their size on every pointer.
 * On a coarse pointer (touch) an invisible absolutely positioned `::after`
 * reaches past each edge so the control owns a 44 px square without taking
 * layout space. Reach per drawn size (each `COARSE_HIT_<size>` below):
 *
 *   drawn 16 px → `-inset-3.5` (14 px)    drawn 28 px → `-inset-2`   (8 px)
 *   drawn 22 px → `-inset-[11px]` (11 px) drawn 32 px → `-inset-1.5` (6 px)
 *   drawn 24 px → `-inset-2.5` (10 px)    drawn 36 px → `-inset-1`   (4 px)
 *
 * Two flavours, after the `RowActions` precedent:
 *
 * - BEHIND (`COARSE_HIT_<size>`, `COARSE_HIT_BEHIND`) — for controls in a
 *   cluster. The `::after` sits at `-z-10`, under every drawn box, so a tap on
 *   a visible neighbour never lands on this control. It must live inside an
 *   `isolate` stacking context: either the cluster's DIRECT flex parent (so no
 *   hit area paints over a neighbour's drawn box), or the control itself when
 *   it stands alone. Neighbours in a cluster are spaced on a coarse pointer by
 *   twice the reach, so two hit areas meet edge to edge.
 * - ALONE (`COARSE_HIT_ALONE`) — for a control that stands alone. The hit area
 *   is painted at the button's own level, so it may cover non-interactive text
 *   beside it, which is fine, but it must not sit next to another control or
 *   it steals that control's edge.
 *
 * The two bare bases (`COARSE_HIT_BEHIND`, `COARSE_HIT_ALONE`) take the reach
 * as a literal Tailwind class at the call site, so the scanner sees it, for a
 * one-axis or uneven reach no sized constant covers.
 *
 * A `-z-10` ::after loses to any in-flow box that overlaps it (the browser
 * hit-tests in paint order), so where such a box sits in the way the button's
 * own box grows instead, or the ALONE flavour is used — see the `TaskRow`
 * series list and DECISIONS 2026-10-09 "where the -z-10 pattern fails".
 *
 * jsdom cannot hit-test, so tests pin these classes; a new hit area is
 * verified with `elementFromPoint` in a real browser. Tests pin them with
 * `missingHitClasses` below, against these constants rather than a local copy.
 *
 * This is the only file that spells the coarse `::after` base (#3167); every
 * component builds its hit area from the constants here, and
 * `touch-target.test.ts` fails on a private copy anywhere else.
 */
const COARSE_STANDALONE =
  "relative [@media(pointer:coarse)]:after:absolute [@media(pointer:coarse)]:after:content-['']";
const COARSE_AFTER = `${COARSE_STANDALONE} [@media(pointer:coarse)]:after:-z-10`;

/** ALONE base: a same-level hit area. The caller adds its reach as a literal class. */
export const COARSE_HIT_ALONE = COARSE_STANDALONE;
/** BEHIND base: a `-z-10` hit area. The caller adds its reach as a literal class. */
export const COARSE_HIT_BEHIND = COARSE_AFTER;

/** A 16 px control (a bare 16 px icon): 14 px past each edge → 44 px. */
export const COARSE_HIT_16 = `${COARSE_AFTER} [@media(pointer:coarse)]:after:-inset-3.5`;

/** A 22 px control (a 14 px icon in `p-1`): 11 px past each edge → 44 px. Neighbours sit 22 px apart on touch. */
export const COARSE_HIT_22 = `${COARSE_AFTER} [@media(pointer:coarse)]:after:-inset-[11px]`;
/** A 24 px control (h-6 w-6): 10 px past each edge → 44 px. */
export const COARSE_HIT_24 = `${COARSE_AFTER} [@media(pointer:coarse)]:after:-inset-2.5`;
/** A 28 px control (h-7 w-7): 8 px past each edge → 44 px. */
export const COARSE_HIT_28 = `${COARSE_AFTER} [@media(pointer:coarse)]:after:-inset-2`;
/** A 32 px control (the chat composer's `--chat-control-size`): 6 px past each edge → 44 px. */
export const COARSE_HIT_32 = `${COARSE_AFTER} [@media(pointer:coarse)]:after:-inset-1.5`;
/** A 28 px tall text button that is already wide enough: 8 px above and below → 44 px. */
export const COARSE_HIT_Y_28 = `${COARSE_AFTER} [@media(pointer:coarse)]:after:-inset-y-2`;
/** A 36 px tall text button (ui Button `size="sm"`): 4 px above and below → 44 px. */
export const COARSE_HIT_Y_36 = `${COARSE_AFTER} [@media(pointer:coarse)]:after:-inset-y-1`;
/**
 * A 36 px tall outline text button (ui Button `size="sm" variant="outline"`).
 * The ::after insets from the padding box, which the 1 px border shrinks to
 * 34 px, so it reaches 5 px above and below → 44 px. Neighbours stacked with
 * it need a coarse gap of at least the two reaches added together (10 px
 * between two outline buttons, 9 px beside a borderless one).
 */
export const COARSE_HIT_Y_36_OUTLINE = `${COARSE_AFTER} [@media(pointer:coarse)]:after:-inset-y-[5px]`;
/** A 40 px wide, 44 px tall control: 2 px left and right → 44 px. */
export const COARSE_HIT_X_40 = `${COARSE_AFTER} [@media(pointer:coarse)]:after:-inset-x-0.5`;
/**
 * The shared `ui/switch` (36x20 track with a 2 px border, so a 32x16 padding
 * box, which is what `inset` measures from): 6 px left and right, 14 px above
 * and below → 44 px. The switch is its own `isolate` context, so the hit area
 * needs no cluster around it; rows that stack switches must sit ≥ 44 px apart.
 */
export const COARSE_HIT_SWITCH = `isolate ${COARSE_AFTER} [@media(pointer:coarse)]:after:-inset-x-1.5 [@media(pointer:coarse)]:after:-inset-y-3.5`;
/**
 * The 16 px `ui/checkbox` (1 px border, so a 14 px padding box): 15 px past
 * each edge → 44 px. Its own `isolate` context, so the hit area paints above
 * the in-flow cell beside it rather than under it.
 */
export const COARSE_HIT_CHECKBOX = `isolate ${COARSE_AFTER} [@media(pointer:coarse)]:after:-inset-[15px]`;
/**
 * A 36 px square outline `Button` (`h-9 w-9`, 1 px border, so a 34 px padding
 * box): 5 px past each edge → 44 px. Its own `isolate` context; a toolbar's
 * `gap-2` (8 px) keeps the reach off its neighbours.
 */
export const COARSE_HIT_36_OUTLINE = `isolate ${COARSE_AFTER} [@media(pointer:coarse)]:after:-inset-[5px]`;
/**
 * Size-independent: at least 44 x 44 px on a coarse pointer, whatever the
 * drawn size (#2698, the public top bar). The hit area is centred on the
 * control and measures `max(100%, 44px)` on each axis, so a text pill whose
 * height moves with the font or the language (Sign in at 13 or 14 px,
 * "Iniciar sesión") still reaches 44 px, and an axis already 44 px or more
 * gains nothing. The control is its own `isolate` context, positioned, so the
 * `-z-10` hit area paints with it, above in-flow siblings such as the
 * `.topnav` row (where the bare `-z-10` ones lost the hit test). Neighbours
 * need a coarse gap at least the reach of the larger one (5 px for a 34 px
 * padding box); the top bar's 8 px phone gap covers the theme toggle.
 */
export const COARSE_HIT_44 = `isolate ${COARSE_AFTER} [@media(pointer:coarse)]:after:left-1/2 [@media(pointer:coarse)]:after:top-1/2 [@media(pointer:coarse)]:after:-translate-x-1/2 [@media(pointer:coarse)]:after:-translate-y-1/2 [@media(pointer:coarse)]:after:w-[max(100%,44px)] [@media(pointer:coarse)]:after:h-[max(100%,44px)]`;

/**
 * The classes of a hit-area constant that `className` lacks (#3167). Tests
 * call `expect(missingHitClasses(el.className, COARSE_HIT_24)).toEqual([])`
 * so they pin the shared string instead of a copied class list, and a failure
 * names the missing class.
 */
export function missingHitClasses(className: string, hit: string): string[] {
  const have = new Set(className.split(/\s+/));
  return hit.split(/\s+/).filter((c) => c && !have.has(c));
}
